import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getScheduledReportsListReset } from 'actions/reportsActions';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Reports Schedule Account Selection Hook
 *
 * Manages account selection state and related operations.
 * Handles:
 * - Account selection state
 * - Contract name/number tracking
 * - Account selection handler with GTM tracking
 * - Reset functionality
 *
 * @param params Hook dependencies
 * @returns Account selection state and handlers
 */
export const useReportsScheduleAccountSelection = ({
  allAccounts,
  loggedInUser,
  assignedAccountNumber,
  setPageNumber,
  selectedContractNumber,
  setSelectedContractNumber,
}: {
  allAccounts: any[];
  loggedInUser: any;
  assignedAccountNumber: string;
  setPageNumber: (value: number) => void;
  selectedContractNumber: string | null;
  setSelectedContractNumber: (value: string | null) => void;
}) => {
  const dispatch = useDispatch();

  const [isAccountSelected, setIsAccountSelected] = useState(false);
  const [selectedContractName, setSelectedContractName] = useState('');

  const handleSelectAccount = useCallback(
    (accountNumber: string | null) => {
      setPageNumber(1);

      if (accountNumber) {
        trackCustomEvent(
          'Select Account Dropdown',
          loggedInUser,
          assignedAccountNumber,
          'Selected an account to view related Report Schedules.',
          { selectedAccount: accountNumber }
        );
        const account = allAccounts.find((x) => x.contractNumber === accountNumber);
        if (account) {
          setIsAccountSelected(true);
          setSelectedContractNumber(account.contractNumber);
          setSelectedContractName(account.contractName);
        }
      } else {
        setIsAccountSelected(false);
        setSelectedContractNumber(null);
        setSelectedContractName('');
        dispatch(getScheduledReportsListReset());
      }
    },
    [allAccounts, loggedInUser, assignedAccountNumber, setPageNumber, dispatch]
  );

  return {
    isAccountSelected,
    selectedContractNumber,
    selectedContractName,
    setIsAccountSelected,
    setSelectedContractNumber,
    setSelectedContractName,
    handleSelectAccount,
  };
};
